import { zodResolver } from "@hookform/resolvers/zod";
import {
  type SignInOutput as _SignInOutput,
  signIn,
  signOut,
} from "aws-amplify/auth";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useWithLoading } from "./use-with-loading";

export const SignInFormSchema = z.object({
  email: z.string().email({
    message: "メールアドレスを入力してください",
  }),
  password: z.string().min(6, {
    // 旧ポリシーのアカウントがあるので6文字でvalidationをする
    message: "パスワードが不適切です",
  }),
});
export type SignInFormSchemaType = z.infer<typeof SignInFormSchema>;

export type SignInOutput = _SignInOutput & {
  email: string;
};

type UseSignInFormProps = {
  defaultValues: SignInFormSchemaType;
  onMutation?: VoidFunction;
  onSuccessToSignIn?: VoidFunction;
  onRequireNewPassword?: (result: SignInOutput) => void;
  onRequireTwoFactor?: VoidFunction;
};

export const useSignInForm = ({
  defaultValues,
  onMutation,
  onSuccessToSignIn,
  onRequireNewPassword,
  onRequireTwoFactor,
}: UseSignInFormProps) => {
  const form = useForm<SignInFormSchemaType>({
    resolver: zodResolver(SignInFormSchema),
    defaultValues,
  });

  const handleSubmit = useWithLoading(async (values: SignInFormSchemaType) => {
    onMutation?.();

    try {
      await signOut().catch(() => undefined);
      const result = await signIn({
        username: values.email,
        password: values.password,
      });
      if (
        result.nextStep.signInStep ===
        "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
      ) {
        onRequireNewPassword?.({ ...result, email: values.email });
      } else if (
        result.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE"
      ) {
        onRequireTwoFactor?.();
      } else {
        onSuccessToSignIn?.();
      }
    } catch (e) {
      if (e instanceof Error) {
        form.setError("root", {
          // Errorインスタンスであれば、安全に .message プロパティにアクセスできる
          message:
            e.message ||
            "エラーが発生しました。再度メールアドレスとパスワードを入力してください",
        });
        // メッセージ本文は転送先のログに英語原文が残るため、例外の種別のみ記録する
        console.error("Sign-in failed:", e.name);
      } else {
        // Errorインスタンスでない、予期せぬ例外の場合
        form.setError("root", {
          message:
            "予期せぬエラーが発生しました。再度メールアドレスとパスワードを入力してください",
        });
        console.error("Caught a non-error object:", e);
      }
      await signOut().catch(() => undefined);
    }
  });

  return {
    form,
    handleSubmit: form.handleSubmit(handleSubmit),
    isLoading: handleSubmit.isLoading,
  };
};
